import { useMemo } from "react";
import { useInfiniteQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithDetailedLog } from "src/utils/zodErrorLogger";
import { buildAuditLogParams, type AuditLogFilters } from "./auditLogFilters";
import { AuditLogFeedSchema, type AuditLogEntry, type AuditLogFeed } from "./auditLogSchemas";

export const getAuditLogFeedKey = "getAuditLogFeed" as const;

const ENDPOINT = "/api/audit-logs";
const PAGE_SIZE = 50;

type Options = {
    enabled?: boolean;
    pageSize?: number;
};

const fetchAuditLogPage = async (
    params: Record<string, string>,
    pageSize: number,
    cursor: string | undefined,
): Promise<AuditLogFeed> => {
    const response = await loggedInApiCall.get(ENDPOINT, {
        params: {
            ...params,
            limit: pageSize,
            ...(cursor === undefined ? {} : { cursor }),
        },
    });

    return zodParseWithDetailedLog(response.data, AuditLogFeedSchema, {
        operation: "GET",
        endpoint: ENDPOINT,
        expectedSchema: "AuditLogFeed",
    });
};

export const useGetAuditLogFeed = (filters: AuditLogFilters, options?: Options) => {
    const { enabled = true, pageSize = PAGE_SIZE } = options ?? {};
    const params = buildAuditLogParams(filters);

    const query = useInfiniteQuery<AuditLogFeed>({
        queryKey: [getAuditLogFeedKey, params, pageSize],
        enabled,
        keepPreviousData: true,
        queryFn: ({ pageParam }) =>
            fetchAuditLogPage(params, pageSize, typeof pageParam === "string" ? pageParam : undefined),
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    });

    const pages = query.data?.pages ?? [];
    const entries = useMemo(() => pages.flatMap((page) => page.items), [query.data]);

    return {
        entries,
        facets: pages[0]?.facets,
        ...query,
    };
};

export const fetchAllAuditLogEntries = async (
    filters: AuditLogFilters,
    pageSize: number = PAGE_SIZE,
): Promise<AuditLogEntry[]> => {
    const params = buildAuditLogParams(filters);
    const entries: AuditLogEntry[] = [];
    let cursor: string | undefined;

    do {
        const page = await fetchAuditLogPage(params, pageSize, cursor);

        entries.push(...page.items);
        cursor = page.nextCursor ?? undefined;
    } while (cursor !== undefined);

    return entries;
};
